import { useState, useEffect, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { Eyebrow } from "@/components/Eyebrow";
import { Loader2, RefreshCw, Feather, Check, X, Quote, AlertCircle, Sparkles } from "lucide-react";

const LABEL = { fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--tmuted)", fontWeight: 600 };

export function VoiceCardPanel({ brandId, initialVoiceCard }) {
  const [card, setCard] = useState(initialVoiceCard || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = useCallback(async (force) => {
    if (!brandId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/brand/voice-card", { brandId, force: !!force });
      setCard(data.voiceCard);
    } catch (e) {
      setError(errMsg(e, "Could not generate the voice card"));
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    setCard(initialVoiceCard || null);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const tone = card?.tone || [];
  const dos = card?.dos || [];
  const donts = card?.donts || [];
  const phrases = card?.phrases || [];

  return (
    <div className="panel-card" style={{ padding: 22 }} data-testid="voice-card-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow testId="eyebrow-voice">Quill · Brand Voice Card</Eyebrow>
          <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "6px 0 0" }}>
            A reusable voice profile injected into every idea, caption &amp; revision so everything sounds on-brand.
          </p>
        </div>
        {card && !loading && (
          <button onClick={() => run(true)} className="btn-secondary font-mono" data-testid="voice-rerun" style={{ fontSize: 11, padding: "7px 11px", flexShrink: 0 }}>
            <RefreshCw size={12} /> Regenerate
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3" data-testid="voice-loading" style={{ padding: "26px 4px", color: "var(--tsecondary)" }}>
          <Loader2 size={18} className="spin" />
          <span className="font-body" style={{ fontSize: 13.5 }}>Quill is distilling your brand voice…</span>
        </div>
      )}

      {!loading && error && (
        <div data-testid="voice-error" style={{ marginTop: 16 }}>
          <div className="flex items-center gap-2 font-body" style={{ fontSize: 13, color: "var(--err)" }}>
            <AlertCircle size={15} /> {error}
          </div>
          <button onClick={() => run(true)} className="btn-secondary font-mono" style={{ fontSize: 11, padding: "7px 11px", marginTop: 12 }}>
            <RefreshCw size={12} /> Try again
          </button>
        </div>
      )}

      {!loading && !error && !card && (
        <div data-testid="voice-empty" style={{ marginTop: 18, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
          <div className="flex items-center gap-2.5" style={{ color: "var(--tsecondary)" }}>
            <Feather size={18} />
            <span className="font-body" style={{ fontSize: 13.5 }}>No voice card yet — let Quill build one from this brand's DNA.</span>
          </div>
          <button onClick={() => run(false)} className="btn-primary font-mono" data-testid="voice-generate" style={{ fontSize: 12.5, padding: "10px 16px" }}>
            <Sparkles size={14} /> Generate voice card
          </button>
        </div>
      )}

      {!loading && card && (
        <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 22 }}>
          {card.summary && (
            <div data-testid="voice-summary" style={{ padding: "14px 16px", borderRadius: 10, border: "1px solid var(--cardborder)", background: "var(--surface)" }}>
              <p className="font-body" style={{ fontSize: 14.5, color: "var(--tprimary)", margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>“{card.summary}”</p>
            </div>
          )}

          {tone.length > 0 && (
            <div data-testid="voice-tone">
              <div className="flex items-center gap-2" style={LABEL}><Feather size={13} /> Tone</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 10 }}>
                {tone.map((t, i) => (
                  <span key={i} className="font-mono" style={{ fontSize: 12, padding: "5px 11px", borderRadius: 999, background: "var(--panel)", border: "1px solid var(--cardborder)", color: "var(--tprimary)", textTransform: "capitalize" }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {(dos.length > 0 || donts.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {dos.length > 0 && (
                <div data-testid="voice-dos">
                  <div className="flex items-center gap-1.5 font-mono" style={{ fontSize: 10.5, color: "var(--ok)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}><Check size={12} /> Do</div>
                  <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 5 }}>
                    {dos.map((d, i) => <li key={i} className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", lineHeight: 1.45 }}>{d}</li>)}
                  </ul>
                </div>
              )}
              {donts.length > 0 && (
                <div data-testid="voice-donts">
                  <div className="flex items-center gap-1.5 font-mono" style={{ fontSize: 10.5, color: "var(--err)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}><X size={12} /> Don't</div>
                  <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 5 }}>
                    {donts.map((d, i) => <li key={i} className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", lineHeight: 1.45 }}>{d}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {phrases.length > 0 && (
            <div data-testid="voice-phrases">
              <div className="flex items-center gap-2" style={LABEL}><Quote size={13} /> Sample phrases</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                {phrases.map((p, i) => (
                  <div key={i} className="font-body" style={{ fontSize: 13, color: "var(--tprimary)", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--cardborder)", background: "var(--panel)", lineHeight: 1.45 }}>“{p}”</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
